/**
 * Chat attachment uploads — multipart POST endpoints.
 *
 * Two endpoints (kept distinct so the BE can enforce different size
 * limits + storage tiers):
 *   - POST `/uploads/chat-image` — inline vision (Apr 2026, MinIO-backed,
 *     max 10MB, response: ChatImageAttachment)
 *   - POST `/uploads/chat-file`  — generic file (May 2026,
 *     content_items row, max 25MB, response: ChatFileAttachment)
 *
 * Plus the page-driven generic upload helper:
 *   - POST `/uploads` — workspace document upload (May 2026 page modal,
 *     content_items row + Temporal ingestion workflow, response:
 *     UploadDocumentResponse). Implemented over XHR so the FE can
 *     surface a native byte-level progress event — fetch() does not
 *     expose upload progress as of Chrome 125 / Firefox 125.
 *
 * The browser MUST send the device fingerprint header so the BE can
 * scope the upload to the same session that authenticated the cookie
 * (mirrors WSClient's `device_id` query param). The fingerprint is
 * best-effort — failure logs in dev but never blocks the upload.
 *
 * v1 ref: `ando-frontend/app/api/uploads/uploadChatFile.ts` (~90 LoC)
 *         + `ando-frontend/app/api/uploads/uploadChatImage.ts`. Both
 * inlined the device-id header + multipart wiring; v2 consolidates into
 * one helper because the only differences are URL + response type.
 */

import { getDeviceId } from "../../auth/fingerprint.js";
import type { ChatFileAttachment, ChatImageAttachment } from "../../domain/chat/attachment.js";
import { apiClient } from "../ApiClient.js";
import { ApiError, NetworkError } from "../errors.js";

const DEVICE_HEADER = "X-Device-Id";

async function uploadMultipart<T>(
  endpoint: string,
  blob: Blob,
  filename: string,
  conversationId: string | undefined,
  triggerIngestion?: boolean,
): Promise<T> {
  const form = new FormData();
  form.append("file", blob, filename);
  if (conversationId !== undefined) {
    form.append("conversation_id", conversationId);
  }
  // Only sent when the caller has an explicit intent — otherwise the BE
  // default applies. Chat-file uploads pass ``true`` so a dropped file is
  // always RAG-indexed (searchable + navigable in later turns), instead of
  // relying on the server-side default staying ``True``.
  if (triggerIngestion !== undefined) {
    form.append("trigger_ingestion", triggerIngestion ? "true" : "false");
  }

  let deviceId: string | null = null;
  try {
    deviceId = await getDeviceId();
  } catch {
    // Best-effort; the BE will reject if a fingerprint is mandatory.
  }

  const headers: Record<string, string> = {};
  if (deviceId) headers[DEVICE_HEADER] = deviceId;

  return await apiClient.postMultipart<T>(endpoint, form, { headers });
}

export async function uploadChatImage(
  blob: Blob,
  filename: string,
  conversationId?: string,
): Promise<ChatImageAttachment> {
  return uploadMultipart<ChatImageAttachment>(
    "/uploads/chat-image",
    blob,
    filename,
    conversationId,
  );
}

/**
 * GET ``/uploads/chat-image?key=...`` — fetch an already-uploaded chat
 * image as a Blob (auth-bearing). The BE tenant-isolates by checking
 * the key prefix matches the requesting user's namespace; a path that
 * doesn't start with ``{user_id}/chat/`` 422s.
 *
 * Returns a fresh ``Blob`` — the caller is responsible for converting
 * it to an object URL via ``URL.createObjectURL`` and revoking the URL
 * on unmount to avoid memory leaks. See ``ChatImageThumbnails`` in
 * ``apps/client/src/features/chat/components/`` for the canonical
 * consumption pattern (mirrors ``useInlineImageMap`` for email inline
 * images).
 */
export async function getChatImageBlob(minioKey: string): Promise<Blob> {
  const path = `/uploads/chat-image?key=${encodeURIComponent(minioKey)}`;
  return apiClient.requestBlob(path, { method: "GET" });
}

/**
 * Raw BE response shape for ``POST /uploads`` (the chat-file path —
 * branched by ``conversation_id`` form field, not a distinct route).
 * The BE re-uses ``UploadResponse`` with page-upload + chat callers,
 * so the field names differ from the FE-facing ``ChatFileAttachment``
 * (``content_type`` vs ``mime_type``, ``ingestion_status`` optional).
 * Adapter at the boundary keeps every consumer typed on the FE shape.
 */
interface UploadResponseWire {
  readonly content_id?: string | null;
  readonly doc_id: string;
  readonly filename: string;
  readonly content_type: string;
  readonly size_bytes: number;
  readonly ingestion_status?: string | null;
  /** Present when the upload was a .zip expanded into member files server-side. */
  readonly extracted?: ReadonlyArray<{
    readonly content_id?: string | null;
    readonly skipped?: boolean;
  }> | null;
}

/** Cap on member ids carried to the agent for an expanded archive. The BE caps
 *  again at ``MAX_FILE_ATTACHMENTS``; this just bounds the wire payload. */
const MAX_ARCHIVE_MEMBER_IDS = 50;

export async function uploadChatFile(
  blob: Blob,
  filename: string,
  conversationId?: string,
): Promise<ChatFileAttachment> {
  // June 2, 2026 — URL alignment: the BE chat-file path is the ROOT
  // ``POST /uploads`` route gated by the ``conversation_id`` form
  // field (no dedicated ``/chat-file`` POST exists; the only route
  // under that prefix is the ``GET /chat-file/{content_id}/url``
  // presigned-URL helper we added in the audio MVP). Hitting
  // ``/uploads/chat-file`` returned 405 because FastAPI matched the
  // path prefix but rejected the method — symptom: every voice-note
  // failed with "Method Not Allowed".
  const raw = await uploadMultipart<UploadResponseWire>(
    "/uploads",
    blob,
    filename,
    conversationId,
    // Always RAG-index chat-dropped files (explicit intent, not the BE
    // default) so they become searchable + section-navigable in later turns.
    true,
  );
  return {
    // ``content_id`` is the canonical chat-file handle. The BE makes
    // it optional because legacy page-upload callers don't carry one;
    // for the chat path it's always populated.
    content_id: raw.content_id ?? raw.doc_id,
    filename: raw.filename,
    mime_type: raw.content_type,
    size_bytes: raw.size_bytes,
    // Default to ``pending`` when the BE omits it (chat-file with
    // async Temporal ingestion) so the FE chip can show the spinner
    // instead of "unknown".
    ingestion_status: (raw.ingestion_status as ChatFileAttachment["ingestion_status"]) ?? "pending",
    // Surface the member count + ids for a .zip: the chip reads "N file" and at
    // send the ids REPLACE the (non-readable) archive handle so the agent gets
    // the real members via fetch_chat_file_refs (June 25-26, 2026).
    ...(Array.isArray(raw.extracted)
      ? {
          extracted_count: raw.extracted.length,
          extracted_ids: raw.extracted
            .filter((m) => m && !m.skipped && m.content_id)
            .map((m) => m.content_id as string)
            .slice(0, MAX_ARCHIVE_MEMBER_IDS),
        }
      : {}),
  };
}

export interface ChatFilePresignedUrl {
  /** Short-lived MinIO GET URL. Pipe straight into ``<audio src=…>`` /
   *  ``<a href=…>``. Never log this to telemetry — it's a signed token. */
  readonly url: string;
  /** TTL in seconds the BE granted (defaults to 3600). */
  readonly expires_in: number;
  readonly mime_type: string | null;
  readonly filename: string | null;
}

/**
 * Fetch a presigned playback URL for a chat-file attachment (audio
 * voice-note, PDF preview, …). Added June 1, 2026 so the chat history
 * can play audio without proxying bytes through the BE.
 */
export async function getChatFileUrl(
  contentId: string,
  options: { expiresIn?: number } = {},
): Promise<ChatFilePresignedUrl> {
  const params = new URLSearchParams();
  if (options.expiresIn !== undefined) {
    params.set("expires_in", String(options.expiresIn));
  }
  const qs = params.toString();
  return apiClient.get<ChatFilePresignedUrl>(
    qs
      ? `/uploads/chat-file/${encodeURIComponent(contentId)}/url?${qs}`
      : `/uploads/chat-file/${encodeURIComponent(contentId)}/url`,
  );
}

/**
 * Fetch a chat-file's BYTES through the backend auth-proxy as a ``Blob``.
 *
 * In-browser previews (PDF viewer, images) must NOT use the presigned URL:
 * ``MINIO_PUBLIC_ENDPOINT`` host-swaps the signed URL post-signature, and
 * SigV4 signs the ``Host`` header → ``SignatureDoesNotMatch`` on the browser
 * fetch. This hits ``GET /uploads/chat-file/{id}/content`` (authed with the
 * bearer token) and the caller wraps the blob in a ``URL.createObjectURL``.
 */
export async function getChatFileBlob(contentId: string): Promise<Blob> {
  return apiClient.requestBlob(`/uploads/chat-file/${encodeURIComponent(contentId)}/content`, {
    method: "GET",
  });
}

// ─── Workspace document upload (May 2026) ──────────────────────────────
// Backed by BE ``POST /uploads`` (see ``ando-agent``
// ``src/api/v1/endpoints/uploads.py:286``). The response is the BE-side
// ``UploadResponse`` Pydantic model; we mirror only the fields the FE
// actually consumes so the wire-type stays narrow.

export interface UploadDocumentResponse {
  doc_id: string;
  filename: string;
  size_bytes: number;
  content_type: string;
  /** uploaded | processing | completed | failed */
  status: string;
  minio_key: string;
  upload_time: string;
  ingestion_status?: string | null;
  /** Temporal workflow job ID — present when ingestion was triggered. */
  job_id?: string | null;
  /** content_items.id (UUID), canonical for chat-files and FE-side joins. */
  content_id?: string | null;
}

export interface UploadDocumentOptions {
  /** Virtual folder path stored on the content_items row. */
  folderPath?: string;
  /** Default ``true`` on the BE — set ``false`` to skip RAG ingestion. */
  triggerIngestion?: boolean;
  /** Optional explicit ingestion mode (phantom | phantom_plus | full | …). */
  ingestionMode?: string;
}

export interface UploadProgressEvent {
  /** Bytes transmitted to the BE so far. */
  loaded: number;
  /** Total bytes the BE will see — equals ``file.size`` when known. */
  total: number;
  /** 0-100 inclusive. */
  percent: number;
}

export interface UploadDocumentParams {
  file: File;
  options?: UploadDocumentOptions;
  /** Progress callback fired on each XHR ``upload.onprogress`` event. */
  onProgress?: (ev: UploadProgressEvent) => void;
  /** Abort the in-flight upload (XHR ``abort()``). */
  signal?: AbortSignal;
}

/**
 * Upload a workspace document via multipart POST with native byte-level
 * progress (XHR — fetch() still doesn't expose upload progress events
 * in any browser as of May 2026).
 *
 * Mirrors the auth + device-id header logic of ``ApiClient.request()``
 * so the BE sees an identical request envelope. Cookie-based BFF auth
 * rides on ``withCredentials = true``; the desktop / Tauri shell
 * additionally attaches a Bearer header read from
 * ``apiClient.getAuthToken()``.
 *
 * Errors surface as the same hierarchy as ``ApiClient``:
 *   - ``NetworkError`` for browser-level failures (offline, CORS).
 *   - ``ApiError`` for BE-side non-2xx with the parsed JSON detail.
 *
 * AbortController support: the caller's signal cancels the XHR
 * (``xhr.abort()``) which surfaces as a ``DOMException`` with name
 * ``AbortError`` — same shape as ``fetch()``.
 */
export async function uploadDocument(
  params: UploadDocumentParams,
): Promise<UploadDocumentResponse> {
  const { file, options, onProgress, signal } = params;

  if (signal?.aborted) {
    throw new DOMException("Upload aborted", "AbortError");
  }

  const form = new FormData();
  form.append("file", file, file.name);
  if (options?.folderPath !== undefined) {
    form.append("folder_path", options.folderPath);
  }
  if (options?.triggerIngestion !== undefined) {
    form.append("trigger_ingestion", options.triggerIngestion ? "true" : "false");
  }
  if (options?.ingestionMode !== undefined) {
    form.append("ingestion_mode", options.ingestionMode);
  }

  const baseUrl = apiClient.getBaseUrl();
  const url = `${baseUrl}/uploads`;

  // Best-effort device-id + auth lookups. Same contract as ApiClient:
  // a failure here NEVER blocks the upload; the BE will reject if
  // the headers are mandatory.
  let deviceId: string | null = null;
  try {
    deviceId = await getDeviceId();
  } catch {
    // ignore
  }
  const bearer = await apiClient.getAuthToken();

  return await new Promise<UploadDocumentResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.withCredentials = true; // BFF httpOnly cookie path
    if (deviceId) xhr.setRequestHeader(DEVICE_HEADER, deviceId);
    if (bearer) xhr.setRequestHeader("Authorization", `Bearer ${bearer}`);
    // No Content-Type header — the browser injects the multipart boundary.

    xhr.upload.addEventListener("progress", (ev: ProgressEvent) => {
      if (!onProgress) return;
      const total = ev.lengthComputable && ev.total > 0 ? ev.total : file.size;
      const loaded = ev.loaded;
      const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
      onProgress({ loaded, total, percent });
    });

    xhr.addEventListener("load", () => {
      // 2xx → resolve; non-2xx → reject with ApiError mirroring ApiClient
      const status = xhr.status;
      const rawText = xhr.responseText ?? "";
      let parsed: unknown = null;
      if (rawText.length > 0) {
        try {
          parsed = JSON.parse(rawText);
        } catch {
          parsed = rawText;
        }
      }
      if (status >= 200 && status < 300) {
        // Fire a final 100% event so the UI always lands on done. Some
        // XHR implementations skip the trailing 100% progress tick.
        if (onProgress) {
          onProgress({ loaded: file.size, total: file.size, percent: 100 });
        }
        resolve(parsed as UploadDocumentResponse);
        return;
      }
      // Reuse the structured detail picker shape so callers can grep on
      // ``error.message`` consistently with the fetch path.
      const detail = extractDetail(parsed) ?? `HTTP ${status}: ${xhr.statusText}`;
      reject(new ApiError(detail, status, undefined, parsed));
    });

    xhr.addEventListener("error", () => {
      reject(new NetworkError("Upload failed (network error)", undefined));
    });

    xhr.addEventListener("abort", () => {
      reject(new DOMException("Upload aborted", "AbortError"));
    });

    const onAbort = (): void => {
      try {
        xhr.abort();
      } catch {
        // ignore — abort is best-effort
      }
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.send(form);
  });
}

function extractDetail(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const candidate = body as Record<string, unknown>;
  for (const key of ["detail", "message", "error"] as const) {
    const v = candidate[key];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

// ─── Ingestion job status (async Temporal) ─────────────────────────────
// Backed by BE ``GET /uploads/jobs/{job_id}`` (``uploads.py::get_job_status``
// → ``job_helpers.get_detailed_job_status``). Lets the FE surface a live
// progress toast for the async Temporal ingestion that ``POST /uploads``
// dispatches (``trigger_ingestion=true``).

export interface UploadJobStatus {
  job_id: string;
  /** pending | processing | completed | failed (job-level). */
  status: string;
  source: string;
  total_files: number;
  files_completed: number;
  files_failed: number;
  files_pending: number;
  /** 0-100. */
  progress_percent: number;
  total_chunks_indexed: number;
  created_at?: string | null;
  completed_at?: string | null;
}

/** GET ``/uploads/jobs/{job_id}`` — current ingestion-job progress. */
export async function getUploadJobStatus(jobId: string): Promise<UploadJobStatus> {
  return apiClient.get<UploadJobStatus>(`/uploads/jobs/${encodeURIComponent(jobId)}`);
}
